import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import Icon from '../components/ui/Icons';
import useReveal from '../components/ui/Reveal';
import COMPANY, { ROUTES } from '../config/company';

import heroImage from '../assets/Images/heroTruckImage.png';
import dryVan from '../assets/Images/servicePic1.png';
import reefer from '../assets/Images/servicePic2.png';
import flatbed from '../assets/Images/servicePic3.png';

export const TRAILERS = [
  {
    id: 'dry-van',
    name: 'Dry Van',
    badge: 'Most popular',
    icon: 'box',
    price: '$699.99',
    image: dryVan,
    short: '53′ air-ride dry vans for general freight. Swing or roll-up doors, logistics posts, DOT-inspected.',
    specs: [
      ['Length', '53′ (48′ on request)'],
      ['Suspension', 'Air-ride'],
      ['Doors', 'Swing or roll-up'],
      ['Floor', 'Hardwood / composite'],
      ['Inspection', 'Current DOT annual'],
    ],
    bestFor: ['General freight', 'Drop-and-hook', 'Dedicated lanes', 'Seasonal surge capacity'],
  },
  {
    id: 'reefer',
    name: 'Reefer',
    badge: 'Temperature controlled',
    icon: 'snowflake',
    price: '$1,349.99',
    image: reefer,
    short: '53′ refrigerated trailers with late-model Thermo King / Carrier units. Food-grade, washout-ready.',
    specs: [
      ['Length', '53′'],
      ['Unit', 'Thermo King / Carrier'],
      ['Range', '−20°F to +70°F'],
      ['Floor', 'Duct aluminum, food-grade'],
      ['Inspection', 'Current DOT annual'],
    ],
    bestFor: ['Produce & grocery', 'Frozen foods', 'Pharma & chemicals', 'Multi-temp loads'],
  },
  {
    id: 'flatbed',
    name: 'Flatbed',
    badge: 'Open deck',
    icon: 'flat',
    price: '$1,149.99',
    image: flatbed,
    short: '48′–53′ aluminum and combo flatbeds with winches, straps and tarp-ready decks.',
    specs: [
      ['Length', '48′ / 53′'],
      ['Deck', 'Aluminum / steel combo'],
      ['Capacity', 'Up to 48,000 lb'],
      ['Securement', 'Winches, chain ties'],
      ['Inspection', 'Current DOT annual'],
    ],
    bestFor: ['Building materials', 'Machinery', 'Steel & lumber', 'Oversize-ready'],
  },
];

const INCLUDED = [
  'DOT annual inspection current at pickup',
  'Trailer maintenance covered during rental (tires excluded)',
  'No mileage fees, no hidden charges',
  'Same-day pickup when available',
  'Extend or return by email',
  'Roadside support line, 24/7',
];

const FAQ = [
  ['What do I need to rent a trailer?', 'An active MC or USDOT authority, a certificate of insurance listing trailer interchange coverage (minimum $30,000), a valid CDL for the driver, and a signed rental agreement. You can complete all of it on the carrier sign-up page.'],
  ['How is pricing calculated?', 'Trailers are billed at a flat monthly rate with no mileage fees. Weekly rates are available on request. The rate you are quoted is the rate you pay; taxes and any optional add-ons are itemized before you sign.'],
  ['Can I extend my rental?', 'Yes. Contact us by email before the return date and we will extend on the same terms. If you have opted in to SMS updates we will remind you a few days before your return date.'],
  ['Who handles maintenance?', 'We do. Scheduled maintenance and mechanical repairs on the trailer are covered for the length of the rental. Trailer tires are the only exclusion and are the responsibility of the lessee.'],
  ['Where do I pick the trailer up?', 'From the yard we assign when you reserve. Pickup location is provided during booking. Pickup location and hours are confirmed on your reservation.'],
];

export function Faq({ items = FAQ, title = 'Frequently asked questions' }) {
  return (
    <section className="bg-ink-50 py-20 sm:py-24">
      <div className="shell grid gap-10 lg:grid-cols-[1fr_1.6fr]">
        <div>
          <div className="eyebrow mb-3">FAQ</div>
          <h2 className="h2">{title}</h2>
          <p className="mt-4 text-ink-600">
            Not answered here? Email {COMPANY.emailDisplay}.
          </p>
        </div>
        <div className="divide-y divide-ink-200 rounded-2xl border border-ink-200 bg-white">
          {items.map(([q, a]) => (
            <details key={q} className="group p-6">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-lg font-bold text-navy-900">
                {q}
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink-100 text-navy-800 transition group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-3 text-[0.95rem] leading-relaxed text-ink-600">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Trailers() {
  useReveal();
  return (
    <>
      <PageHero
        eyebrow="Trailers & pricing"
        title="Three trailer types. One flat rate."
        lede="Every trailer in the Bitnex Freight LLC fleet is DOT-inspected, maintained by us and priced without mileage fees. Pick the one that fits your lane."
        image={heroImage}
      >
        <div className="flex flex-wrap gap-3">
          <Link to={ROUTES.signup} className="btn-primary">
            Reserve a trailer <Icon name="arrowRight" className="h-4 w-4" />
          </Link>
          <a href={COMPANY.phone || COMPANY.email} className="btn-outline-light">
            <Icon name={COMPANY.phone ? 'phone' : 'mail'} className="h-4 w-4" /> {COMPANY.phone ? COMPANY.phoneDisplay : 'Email our team'}
          </a>
        </div>
      </PageHero>

      <section className="py-20 sm:py-24">
        <div className="shell space-y-20">
          {TRAILERS.map((t, i) => (
            <article
              key={t.id}
              id={t.id}
              className="reveal grid scroll-mt-28 items-center gap-10 lg:grid-cols-2"
            >
              <div className={`relative ${i % 2 ? 'lg:order-2' : ''}`}>
                <img
                  src={t.image}
                  alt={`${t.name} trailer`}
                  className="aspect-[4/3] w-full rounded-2xl object-cover shadow-lift"
                  loading="lazy"
                />
                <span className="absolute left-5 top-5 rounded bg-navy-900 px-3 py-1.5 text-[0.72rem] font-bold uppercase tracking-wider text-gold-300">
                  {t.badge}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-lg bg-gold-300 text-navy-900">
                    <Icon name={t.icon} className="h-5 w-5" />
                  </span>
                  <h2 className="h2">{t.name}</h2>
                </div>
                <p className="mt-4 text-lg text-ink-600">{t.short}</p>
                <div className="mt-6 flex items-end gap-2">
                  <span className="font-display text-5xl font-extrabold text-navy-900">{t.price}</span>
                  <span className="pb-2 text-ink-500">/ month, from</span>
                </div>
                <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-ink-200 pt-6 text-[0.9rem] sm:grid-cols-3">
                  {t.specs.map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[0.7rem] font-bold uppercase tracking-wider text-ink-500">{k}</dt>
                      <dd className="font-semibold text-navy-900">{v}</dd>
                    </div>
                  ))}
                </dl>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {t.bestFor.map((b) => (
                    <li key={b} className="rounded-full bg-ink-100 px-3 py-1 text-[0.8rem] font-semibold text-ink-700">
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to={ROUTES.signup} className="btn-dark">
                    Book {t.name.toLowerCase()}
                  </Link>
                  <Link to={ROUTES.contact} className="btn-outline">
                    Ask a question
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="texture-dark bg-navy-900 py-20 text-white sm:py-24">
        <div className="shell grid items-center gap-10 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <div className="eyebrow mb-3 text-gold-300">Every rental includes</div>
            <h2 className="h2 text-white">Flat rate. No surprises.</h2>
            <p className="mt-4 text-navy-100">
              Prices shown are average monthly rates. Weekly terms and volume pricing for fleets are available — call
              us for a quote.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {INCLUDED.map((item) => (
              <li key={item} className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/5 p-4">
                <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-gold-300 text-navy-900">
                  <Icon name="check" className="h-3 w-3" strokeWidth={3} />
                </span>
                <span className="text-[0.92rem] text-navy-50">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Faq />
    </>
  );
}
